import type { Employee, ResourceAllocation, TimeEntry } from "@/types";

import type { TeamMember } from "../types";

/** Actual hours per employee, counting approved/posted time only. */
export function buildApprovedHoursByEmployee(timeEntries: TimeEntry[]): Map<number, number> {
  const timeByEmployee = new Map<number, number>();
  timeEntries.forEach((entry) => {
    const isApproved = entry.normalizedStatus
      ? entry.normalizedStatus === 'approved' || entry.normalizedStatus === 'posted'
      : entry.approved;
    if (!isApproved) return;
    timeByEmployee.set(entry.employeeId, (timeByEmployee.get(entry.employeeId) || 0) + entry.hours);
  });
  return timeByEmployee;
}

/**
 * Team rows for employees allocated to the project or with logged time against it.
 */
export function buildTeamMembers(
  employees: Employee[],
  projectAllocations: ResourceAllocation[],
  timeByEmployee: Map<number, number>,
  excludedEmployeeIds: Set<number>,
): TeamMember[] {
  const allocationsByEmployee = new Map<number, ResourceAllocation>();
  projectAllocations.forEach((alloc) => {
    allocationsByEmployee.set(alloc.employeeId, alloc);
  });

  return employees
    .filter((employee) =>
      (allocationsByEmployee.has(employee.id) || timeByEmployee.has(employee.id))
      && !excludedEmployeeIds.has(employee.id)
    )
    .map((employee) => {
      const allocation = allocationsByEmployee.get(employee.id);
      const actualHours = timeByEmployee.get(employee.id) || 0;
      const allocatedHours = allocation?.allocatedHours || 0;
      const remaining = allocatedHours - actualHours;
      const utilisationPercent = allocatedHours > 0 ? (actualHours / allocatedHours) * 100 : 0;

      let status: TeamMember["status"];
      if (allocatedHours > 0 && utilisationPercent > 100) {
        status = "Over Allocated";
      } else if (allocatedHours > 0 && utilisationPercent > 85) {
        status = "At Risk";
      } else {
        status = "On Track";
      }

      return {
        id: allocation?.id || employee.id,
        employeeId: employee.id,
        name: employee.name,
        email: employee.email || "",
        role: allocation?.role || employee.role || "Unassigned",
        avatar: employee.photo,
        allocatedHours,
        actualHours,
        remaining,
        startDate: allocation?.startDate || "",
        endDate: allocation?.endDate || "",
        status,
        utilisationPercent,
      };
    });
}
